import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Select,
  DatePicker,
  Button,
  Tag,
  Progress,
  Space,
  Tabs,
  Modal,
  Form,
  Input,
  InputNumber,
  message,
  Descriptions,
  Alert,
} from 'antd';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from 'recharts';
import {
  TrophyOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  CalendarOutlined,
  StopOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../lib/api-client';
import { approveBlacklist } from '@/services/supplierLifecycle.service';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { num, unwrapList } from './supplierAssessmentUi';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TabPane } = Tabs;

interface Supplier {
  id: string;
  supplier_name: string;
  supplier_code: string;
}

interface KPISummary {
  kpi_code: string;
  kpi_name: string;
  actual_value: number;
  target_value: number;
  achievement_percentage: number;
  performance_status: string;
}

interface PerformanceSummary {
  supplier_id: string;
  overall_score: number;
  performance_rating: string;
  kpis: KPISummary[];
  recent_performance_records: any[];
}

const COLORS = ['#52c41a', '#faad14', '#f5222d', '#145A54', '#722ed1', '#13c2c2'];

/** MySQL DECIMAL fields arrive as strings — coerce before .toFixed / Progress. */
function normalizePerformanceSummary(raw: unknown): PerformanceSummary | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const kpisRaw = Array.isArray(row.kpis) ? row.kpis : [];
  return {
    supplier_id: String(row.supplier_id ?? ''),
    overall_score: num(row.overall_score, 0),
    performance_rating: String(row.performance_rating ?? 'Poor'),
    kpis: kpisRaw.map((k) => {
      const kpi = (k || {}) as Record<string, unknown>;
      return {
        kpi_code: String(kpi.kpi_code ?? ''),
        kpi_name: String(kpi.kpi_name ?? kpi.kpi_code ?? 'KPI'),
        actual_value: num(kpi.actual_value, 0),
        target_value: num(kpi.target_value, 0),
        achievement_percentage: num(kpi.achievement_percentage, 0),
        performance_status: String(kpi.performance_status ?? ''),
      };
    }),
    recent_performance_records: Array.isArray(row.recent_performance_records)
      ? row.recent_performance_records
      : [],
  };
}

const SupplierPerformanceDashboard: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(3, 'months'),
    dayjs(),
  ]);
  const [performanceSummary, setPerformanceSummary] = useState<PerformanceSummary | null>(null);
  const [suppliersDueForReEvaluation, setSuppliersDueForReEvaluation] = useState<any[]>([]);
  const [blacklistedSuppliers, setBlacklistedSuppliers] = useState<any[]>([]);
  const [scheduleModalVisible, setScheduleModalVisible] = useState(false);
  const [blacklistModalVisible, setBlacklistModalVisible] = useState(false);
  const [scorecardModalVisible, setScorecardModalVisible] = useState(false);
  const [scorecards, setScorecards] = useState<any[]>([]);
  const [scorecardYear, setScorecardYear] = useState(dayjs().year());
  const [form] = Form.useForm();
  const [blacklistForm] = Form.useForm();
  const [scorecardForm] = Form.useForm();

  useEffect(() => {
    fetchSuppliers();
    fetchSuppliersDueForReEvaluation();
    fetchBlacklistedSuppliers();
    void fetchScorecards();
  }, []);

  const fetchScorecards = async (year?: number) => {
    try {
      const y = year ?? scorecardYear;
      const response = await apiClient.get('/procurement/supplier-qms-scorecards', {
        params: { period_year: y },
      });
      setScorecards(unwrapList(response));
    } catch {
      /* optional until migration applied */
    }
  };

  useEffect(() => {
    if (selectedSupplier) {
      fetchPerformanceSummary();
    }
  }, [selectedSupplier, dateRange]);

  const fetchSuppliers = async () => {
    try {
      // apiClient already returns ApiResponse body ({ data: [...] }); do not dig to .data.data
      const response = await apiClient.get('/procurement/suppliers', {
        params: { is_active: 'true' },
      });
      setSuppliers(unwrapList<Supplier>(response));
    } catch (error) {
      message.error('Failed to fetch suppliers');
    }
  };

  const fetchPerformanceSummary = async () => {
    if (!selectedSupplier) return;

    setLoading(true);
    try {
      const response = await apiClient.get(
        `/procurement/suppliers/${selectedSupplier}/performance`,
        {
          params: {
            period_start: dateRange[0].format('YYYY-MM-DD'),
            period_end: dateRange[1].format('YYYY-MM-DD'),
          },
        }
      );
      const payload =
        response && typeof response === 'object' && 'data' in response
          ? (response as { data: unknown }).data
          : response;
      setPerformanceSummary(normalizePerformanceSummary(payload));
    } catch (error) {
      message.error('Failed to fetch performance summary');
      setPerformanceSummary(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliersDueForReEvaluation = async () => {
    try {
      const response = await apiClient.get('/procurement/reevaluations/due');
      setSuppliersDueForReEvaluation(unwrapList(response));
    } catch (error) {
      console.error('Failed to fetch suppliers due for re-evaluation');
    }
  };

  const fetchBlacklistedSuppliers = async () => {
    try {
      const response = await apiClient.get('/procurement/blacklist');
      setBlacklistedSuppliers(unwrapList(response));
    } catch (error) {
      console.error('Failed to fetch blacklisted suppliers');
    }
  };

  const handleCalculateKPIs = async () => {
    if (!selectedSupplier) return;

    setLoading(true);
    try {
      await apiClient.post(`/procurement/suppliers/${selectedSupplier}/calculate-kpis`, {
        period_start: dateRange[0].format('YYYY-MM-DD'),
        period_end: dateRange[1].format('YYYY-MM-DD'),
      });
      message.success('KPIs calculated successfully');
      await fetchPerformanceSummary();
    } catch (error: any) {
      const detail =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        'Failed to calculate KPIs';
      message.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleReEvaluation = async (values: any) => {
    if (!selectedSupplier) return;

    try {
      await apiClient.post(`/procurement/suppliers/${selectedSupplier}/schedule-reevaluation`, {
        schedule_type: values.schedule_type,
        scheduled_date: values.scheduled_date.format('YYYY-MM-DD'),
        priority: values.priority,
        trigger_event: values.trigger_event,
      });
      message.success('Re-evaluation scheduled successfully');
      setScheduleModalVisible(false);
      form.resetFields();
      fetchSuppliersDueForReEvaluation();
    } catch (error) {
      message.error('Failed to schedule re-evaluation');
    }
  };

  const handleBlacklistSupplier = async (values: any) => {
    if (!selectedSupplier) return;

    try {
      await apiClient.post(`/procurement/suppliers/${selectedSupplier}/blacklist`, {
        blacklist_reason: values.blacklist_reason,
        detailed_reason: values.detailed_reason,
        severity: values.severity,
        effective_from: values.effective_from.format('YYYY-MM-DD'),
        is_permanent: values.is_permanent,
      });
      message.success('Supplier blacklisted successfully (pending MD approval)');
      setBlacklistModalVisible(false);
      blacklistForm.resetFields();
      fetchBlacklistedSuppliers();
    } catch (error) {
      message.error('Failed to blacklist supplier');
    }
  };

  const getPerformanceColor = (score: number) => {
    if (score >= 90) return '#52c41a';
    if (score >= 80) return '#145A54';
    if (score >= 70) return '#faad14';
    if (score >= 60) return '#ff7a45';
    return '#f5222d';
  };

  const getStatusTag = (status: string) => {
    const statusConfig: { [key: string]: { color: string; text: string } } = {
      Exceeds: { color: 'green', text: 'Exceeds Target' },
      Meets: { color: 'blue', text: 'Meets Target' },
      Below: { color: 'orange', text: 'Below Target' },
      Critical: { color: 'red', text: 'Critical' },
    };

    const config = statusConfig[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const getRatingIcon = (rating: string) => {
    switch (rating) {
      case 'Excellent':
        return <TrophyOutlined style={{ color: '#52c41a', fontSize: 24 }} />;
      case 'Good':
        return <CheckCircleOutlined style={{ color: '#145A54', fontSize: 24 }} />;
      case 'Average':
        return <SyncOutlined style={{ color: '#faad14', fontSize: 24 }} />;
      case 'Below Average':
        return <WarningOutlined style={{ color: '#ff7a45', fontSize: 24 }} />;
      case 'Poor':
        return <CloseCircleOutlined style={{ color: '#f5222d', fontSize: 24 }} />;
      default:
        return null;
    }
  };

  const kpiColumns: ColumnsType<KPISummary> = [
    {
      title: 'KPI',
      dataIndex: 'kpi_name',
      key: 'kpi_name',
      width: 200,
    },
    {
      title: 'Actual',
      dataIndex: 'actual_value',
      key: 'actual_value',
      width: 100,
      render: (value, record) => {
        const unit = record.kpi_code === 'NCR_COUNT' ? '' : '%';
        return `${num(value).toFixed(2)}${unit}`;
      },
    },
    {
      title: 'Target',
      dataIndex: 'target_value',
      key: 'target_value',
      width: 100,
      render: (value, record) => {
        const unit = record.kpi_code === 'NCR_COUNT' ? '' : '%';
        return `${num(value).toFixed(2)}${unit}`;
      },
    },
    {
      title: 'Achievement',
      dataIndex: 'achievement_percentage',
      key: 'achievement_percentage',
      width: 150,
      render: (value) => {
        const pct = num(value);
        return (
          <Progress
            percent={Math.min(pct, 100)}
            size="small"
            status={pct >= 100 ? 'success' : pct >= 70 ? 'normal' : 'exception'}
          />
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'performance_status',
      key: 'performance_status',
      width: 120,
      render: (status) => getStatusTag(status),
    },
  ];

  const reevaluationColumns: ColumnsType<any> = [
    {
      title: 'Supplier',
      dataIndex: 'supplier_name',
      key: 'supplier_name',
    },
    {
      title: 'Type',
      dataIndex: 'schedule_type',
      key: 'schedule_type',
      render: (type) => <Tag>{type}</Tag>,
    },
    {
      title: 'Scheduled Date',
      dataIndex: 'scheduled_date',
      key: 'scheduled_date',
      render: (date) => dayjs(date).format('DD MMM YYYY'),
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority) => {
        const color = priority === 'High' ? 'red' : priority === 'Medium' ? 'orange' : 'default';
        return <Tag color={color}>{priority}</Tag>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <Tag color="blue">{status}</Tag>,
    },
  ];

  const blacklistColumns: ColumnsType<any> = [
    {
      title: 'Supplier',
      dataIndex: 'supplier_name',
      key: 'supplier_name',
    },
    {
      title: 'Reason',
      dataIndex: 'blacklist_reason',
      key: 'blacklist_reason',
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      render: (severity) => {
        const color = severity === 'Critical' ? 'red' : severity === 'Major' ? 'orange' : 'yellow';
        return <Tag color={color}>{severity}</Tag>;
      },
    },
    {
      title: 'Blacklist Date',
      dataIndex: 'blacklist_date',
      key: 'blacklist_date',
      render: (date) => dayjs(date).format('DD MMM YYYY'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <Tag color="red">{status}</Tag>,
    },
    {
      title: 'MD Approval',
      key: 'md_approval',
      render: (_, record) =>
        !record.approved_by ? (
          <Button
            size="small"
            type="primary"
            onClick={async () => {
              try {
                await approveBlacklist(record.id);
                message.success('Blacklist approved');
                fetchBlacklistedSuppliers();
              } catch {
                message.error('Failed to approve blacklist');
              }
            }}
          >
            Approve
          </Button>
        ) : (
          <Tag color="success">Approved</Tag>
        ),
    },
  ];

  const renderKPIRadarChart = () => {
    const kpis = performanceSummary?.kpis ?? [];
    if (!kpis.length) return null;

    const radarData = kpis.map((kpi) => ({
      subject: kpi.kpi_code,
      A: num(kpi.actual_value),
      fullMark: 100,
    }));

    return (
      <ResponsiveContainer width="100%" height={300}>
        <RadarChart data={radarData}>
          <PolarGrid />
          <PolarAngleAxis dataKey="subject" />
          <PolarRadiusAxis angle={90} domain={[0, 100]} />
          <Radar name="Performance" dataKey="A" stroke="#145A54" fill="#145A54" fillOpacity={0.6} />
          <Tooltip />
        </RadarChart>
      </ResponsiveContainer>
    );
  };

  const renderKPIPieChart = () => {
    const kpis = performanceSummary?.kpis ?? [];
    if (!kpis.length) return null;

    const pieData = kpis.map((kpi) => ({
      name: kpi.kpi_code,
      value: Math.max(num(kpi.achievement_percentage), 0),
    }));

    return (
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={pieData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={({ name, percent }) => `${name}: ${(num(percent) * 100).toFixed(0)}%`}
            outerRadius={80}
            fill="#8884d8"
            dataKey="value"
          >
            {pieData.map((_entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip />
        </PieChart>
      </ResponsiveContainer>
    );
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: 24 }}>
        <h1>Supplier Performance Dashboard</h1>
        <p style={{ color: '#8c8c8c' }}>
          Monitor supplier performance, KPIs, and manage re-evaluations
        </p>
      </div>

      {/* Filters */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16}>
          <Col span={12}>
            <Select
              showSearch
              placeholder="Select Supplier"
              style={{ width: '100%' }}
              value={selectedSupplier}
              onChange={setSelectedSupplier}
              filterOption={(input, option) =>
                String(option?.label ?? option?.children ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {suppliers.map((supplier) => (
                <Option key={supplier.id} value={supplier.id}>
                  {supplier.supplier_name} ({supplier.supplier_code})
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={8}>
            <RangePicker
              value={dateRange}
              onChange={(dates) => dates && setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])}
              style={{ width: '100%' }}
            />
          </Col>
          <Col span={4}>
            <Button type="primary" block onClick={handleCalculateKPIs} loading={loading}>
              Calculate KPIs
            </Button>
          </Col>
        </Row>
      </Card>

      {performanceSummary && (
        <>
          {/* Overall Performance */}
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Overall Score"
                  value={num(performanceSummary.overall_score).toFixed(2)}
                  suffix="%"
                  valueStyle={{
                    color: getPerformanceColor(num(performanceSummary.overall_score)),
                  }}
                  prefix={getRatingIcon(performanceSummary.performance_rating)}
                />
                <div style={{ marginTop: 16 }}>
                  <Tag color={getPerformanceColor(num(performanceSummary.overall_score))}>
                    {performanceSummary.performance_rating}
                  </Tag>
                </div>
              </Card>
            </Col>
            {(performanceSummary.kpis ?? []).slice(0, 3).map((kpi, index) => (
              <Col span={6} key={kpi.kpi_code || index}>
                <Card>
                  <Statistic
                    title={kpi.kpi_name}
                    value={num(kpi.actual_value).toFixed(2)}
                    suffix={kpi.kpi_code === 'NCR_COUNT' ? '' : '%'}
                    valueStyle={{
                      color:
                        kpi.performance_status === 'Exceeds' || kpi.performance_status === 'Meets'
                          ? '#52c41a'
                          : '#f5222d',
                    }}
                  />
                  <div style={{ marginTop: 8 }}>
                    <Progress
                      percent={Math.min(num(kpi.achievement_percentage), 100)}
                      size="small"
                      status={
                        num(kpi.achievement_percentage) >= 100
                          ? 'success'
                          : num(kpi.achievement_percentage) >= 70
                          ? 'normal'
                          : 'exception'
                      }
                    />
                  </div>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Actions */}
          {num(performanceSummary.overall_score) < 70 && (
            <Alert
              message="Poor Performance Detected"
              description="This supplier's performance is below acceptable levels. Consider scheduling a re-evaluation or taking corrective action."
              type="warning"
              showIcon
              style={{ marginBottom: 24 }}
              action={
                <Space>
                  <Button size="small" onClick={() => setScheduleModalVisible(true)}>
                    Schedule Re-evaluation
                  </Button>
                  <Button size="small" danger onClick={() => setBlacklistModalVisible(true)}>
                    Blacklist
                  </Button>
                </Space>
              }
            />
          )}

          <Card style={{ marginBottom: 24 }}>
            <Space style={{ marginBottom: 16 }}>
              <Button
                type="default"
                icon={<CalendarOutlined />}
                onClick={() => setScheduleModalVisible(true)}
              >
                Schedule Re-evaluation
              </Button>
              <Button
                type="default"
                danger
                icon={<StopOutlined />}
                onClick={() => setBlacklistModalVisible(true)}
              >
                Blacklist Supplier
              </Button>
              <Button type="default" icon={<FileTextOutlined />}>
                Generate Report
              </Button>
            </Space>
          </Card>

          {/* Detailed KPIs */}
          <Card title="KPI Performance Details" style={{ marginBottom: 24 }}>
            <Table
              columns={kpiColumns}
              dataSource={performanceSummary.kpis ?? []}
              rowKey="kpi_code"
              pagination={false}
              loading={loading}
            />
          </Card>

          {/* Charts */}
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={12}>
              <Card title="Performance Radar">
                {renderKPIRadarChart()}
              </Card>
            </Col>
            <Col span={12}>
              <Card title="KPI Achievement Distribution">
                {renderKPIPieChart()}
              </Card>
            </Col>
          </Row>
        </>
      )}

      {/* Re-evaluation & Blacklist Tabs */}
      <Card>
        <Tabs defaultActiveKey="1">
          <TabPane tab={`Suppliers Due for Re-evaluation (${suppliersDueForReEvaluation.length})`} key="1">
            <Table
              columns={reevaluationColumns}
              dataSource={suppliersDueForReEvaluation}
              rowKey="id"
              pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
            />
          </TabPane>
          <TabPane tab={`Blacklisted Suppliers (${blacklistedSuppliers.length})`} key="2">
            <Table
              columns={blacklistColumns}
              dataSource={blacklistedSuppliers}
              rowKey="id"
              pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
            />
          </TabPane>
          <TabPane tab={`Annual scorecards (${scorecards.length})`} key="3">
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="Annual supplier performance scorecard"
              description="Four dimensions scored 1–5 (max 20). Automated operational KPIs above remain unchanged. Overall below 50% marks the supplier inactive via the existing is_active flag."
            />
            <Space style={{ marginBottom: 12 }} wrap>
              <Select
                value={scorecardYear}
                style={{ width: 120 }}
                onChange={(y) => {
                  setScorecardYear(y);
                  void fetchScorecards(y);
                }}
                options={[0, 1, 2].map((i) => {
                  const y = dayjs().year() - i;
                  return { value: y, label: String(y) };
                })}
              />
              <Button
                type="primary"
                onClick={() => {
                  scorecardForm.setFieldsValue({
                    period_year: scorecardYear,
                    supplier_id: selectedSupplier || undefined,
                  });
                  setScorecardModalVisible(true);
                }}
              >
                Record scorecard
              </Button>
              <Button
                icon={<FileTextOutlined />}
                onClick={async () => {
                  try {
                    const res = await apiClient.get('/procurement/supplier-qms-scorecards/print', {
                      params: { period_year: scorecardYear },
                    });
                    const payload = res?.data?.data || res?.data;
                    const rows = payload?.rows || [];
                    const body = rows
                      .map(
                        (r: any, i: number) =>
                          `<tr><td>${i + 1}</td><td>${r.supplier_name || ''}</td><td>${r.supplier_code || ''}</td><td>${r.criticality || ''}</td><td>${r.quality_score}</td><td>${r.otd_score}</td><td>${r.communication_score}</td><td>${r.pricing_score}</td><td>${r.overall_score_out_of_20}</td><td>${r.overall_percentage}%</td><td>${r.remarks || ''}</td></tr>`
                      )
                      .join('');
                    const w = window.open('', '_blank');
                    if (!w) return;
                    w.document.write(`<!DOCTYPE html><html><head><title>Supplier Performance Monitoring</title>
                      <style>body{font-family:Segoe UI,Arial,sans-serif;padding:24px}table{border-collapse:collapse;width:100%;font-size:11px}th,td{border:1px solid #333;padding:5px}th{background:#f0f0f0}</style></head><body>
                      <h1>SUPPLIER PERFORMANCE MONITORING FORM</h1>
                      <p>Year: ${scorecardYear} · Printed ${new Date().toLocaleString()}</p>
                      <table><thead><tr><th>Sr</th><th>Supplier</th><th>ID</th><th>Criticality</th><th>Quality</th><th>OTD</th><th>Communication</th><th>Pricing</th><th>/20</th><th>%</th><th>Remarks</th></tr></thead><tbody>${body}</tbody></table>
                      <p style="font-size:11px">Scoring: 1 Very Poor (50-60%) · 2 Poor · 3 Average · 4 Good · 5 Excellent (90-100%)</p>
                      </body></html>`);
                    w.document.close();
                    w.focus();
                    w.print();
                  } catch {
                    message.error('Failed to print scorecards');
                  }
                }}
              >
                Print scorecard
              </Button>
            </Space>
            <Table
              rowKey="id"
              dataSource={scorecards}
              size="small"
              pagination={{ defaultPageSize: 10, showSizeChanger: true }}
              columns={[
                { title: 'Supplier', dataIndex: 'supplier_name' },
                { title: 'Criticality', dataIndex: 'criticality', width: 110 },
                { title: 'Quality', dataIndex: 'quality_score', width: 80 },
                { title: 'OTD', dataIndex: 'otd_score', width: 80 },
                { title: 'Comm.', dataIndex: 'communication_score', width: 80 },
                { title: 'Pricing', dataIndex: 'pricing_score', width: 80 },
                { title: '/20', dataIndex: 'overall_score_out_of_20', width: 70 },
                {
                  title: '%',
                  dataIndex: 'overall_percentage',
                  width: 80,
                  render: (v: number) => `${Number(v || 0).toFixed(1)}%`,
                },
                { title: 'Band', dataIndex: 'performance_band', width: 110 },
              ]}
            />
          </TabPane>
        </Tabs>
      </Card>

      <Modal
        title="Annual performance scorecard"
        open={scorecardModalVisible}
        onCancel={() => {
          setScorecardModalVisible(false);
          scorecardForm.resetFields();
        }}
        onOk={() => scorecardForm.submit()}
        destroyOnHidden
      >
        <Form
          form={scorecardForm}
          layout="vertical"
          onFinish={async (values) => {
            try {
              await apiClient.post('/procurement/supplier-qms-scorecards', values);
              message.success('Scorecard saved');
              setScorecardModalVisible(false);
              scorecardForm.resetFields();
              void fetchScorecards(values.period_year);
            } catch (e: any) {
              message.error(e?.response?.data?.message || 'Failed to save scorecard');
            }
          }}
        >
          <Form.Item name="supplier_id" label="Supplier" rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp="label"
              options={suppliers.map((s) => ({
                value: s.id,
                label: `${s.supplier_code || ''} ${s.supplier_name}`.trim(),
              }))}
            />
          </Form.Item>
          <Form.Item name="period_year" label="Year" rules={[{ required: true }]} initialValue={dayjs().year()}>
            <InputNumber style={{ width: '100%' }} min={2000} max={2100} />
          </Form.Item>
          <Form.Item name="quality_score" label="Product / Service Quality (1–5)" rules={[{ required: true }]}>
            <InputNumber min={1} max={5} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="otd_score" label="On-time delivery (1–5)" rules={[{ required: true }]}>
            <InputNumber min={1} max={5} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="communication_score" label="Communication (1–5)" rules={[{ required: true }]}>
            <InputNumber min={1} max={5} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="pricing_score" label="Pricing (1–5)" rules={[{ required: true }]}>
            <InputNumber min={1} max={5} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Schedule Re-evaluation Modal */}
      <Modal
        title="Schedule Re-evaluation"
        open={scheduleModalVisible}
        onCancel={() => {
          setScheduleModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
      >
        <Form form={form} layout="vertical" onFinish={handleScheduleReEvaluation}>
          <Form.Item
            name="schedule_type"
            label="Schedule Type"
            rules={[{ required: true, message: 'Please select schedule type' }]}
          >
            <Select>
              <Option value="Periodic">Periodic</Option>
              <Option value="Event-Based">Event-Based</Option>
              <Option value="Ad-hoc">Ad-hoc</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="scheduled_date"
            label="Scheduled Date"
            rules={[{ required: true, message: 'Please select date' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="priority"
            label="Priority"
            rules={[{ required: true, message: 'Please select priority' }]}
          >
            <Select>
              <Option value="High">High</Option>
              <Option value="Medium">Medium</Option>
              <Option value="Low">Low</Option>
            </Select>
          </Form.Item>

          <Form.Item name="trigger_event" label="Trigger Event">
            <Input.TextArea rows={3} placeholder="Describe the trigger event..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Blacklist Modal */}
      <Modal
        title="Blacklist Supplier"
        open={blacklistModalVisible}
        onCancel={() => {
          setBlacklistModalVisible(false);
          blacklistForm.resetFields();
        }}
        onOk={() => blacklistForm.submit()}
        okText="Blacklist"
        okButtonProps={{ danger: true }}
      >
        <Alert
          message="Warning"
          description="Blacklisting a supplier requires Managing Director approval. The supplier will be deactivated immediately upon submission."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form form={blacklistForm} layout="vertical" onFinish={handleBlacklistSupplier}>
          <Form.Item
            name="blacklist_reason"
            label="Blacklist Reason"
            rules={[{ required: true, message: 'Please enter reason' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="detailed_reason" label="Detailed Reason">
            <Input.TextArea rows={4} />
          </Form.Item>

          <Form.Item
            name="severity"
            label="Severity"
            rules={[{ required: true, message: 'Please select severity' }]}
          >
            <Select>
              <Option value="Critical">Critical</Option>
              <Option value="Major">Major</Option>
              <Option value="Minor">Minor</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="effective_from"
            label="Effective From"
            rules={[{ required: true, message: 'Please select date' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="is_permanent" valuePropName="checked">
            <input type="checkbox" /> Permanent Blacklist
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SupplierPerformanceDashboard;
